import { useState, useRef } from 'react';
import type { TouchEvent as ReactTouchEvent } from 'react';

interface UseSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  minSwipeDistance?: number;
  onSwipeProgress?: (progress: number) => void;
}

interface SwipeState {
  dragX: number;
  isDragging: boolean;
}

interface SwipeHandlers {
  onTouchStart: (e: ReactTouchEvent) => void;
  onTouchMove: (e: ReactTouchEvent) => void;
  onTouchEnd: () => void;
}

export type UseSwipeReturn = SwipeState & SwipeHandlers;

// Vérifie si l'élément touché est un élément interactif
function isInteractiveElement(element: Element | null): boolean {
  if (!element) return false;

  const interactiveTags = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL']);
  const interactiveRoles = new Set(['button', 'link', 'checkbox', 'radio', 'combobox']);

  if (interactiveTags.has(element.tagName)) return true;

  const role = element.getAttribute('role');
  if (role && interactiveRoles.has(role)) return true;

  if (element.hasAttribute('onclick')) return true;

  let parent = element.parentElement;
  let depth = 0;
  while (parent && depth < 5) {
    if (interactiveTags.has(parent.tagName)) return true;
    const parentRole = parent.getAttribute('role');
    if (parentRole && interactiveRoles.has(parentRole)) return true;
    parent = parent.parentElement;
    depth++;
  }

  return false;
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  minSwipeDistance = 80,
  onSwipeProgress,
}: UseSwipeOptions): UseSwipeReturn {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const shouldIgnoreRef = useRef(false);
  const hasMovedRef = useRef(false);
  const isHorizontalSwipeRef = useRef(false);

  const resetState = () => {
    touchStartRef.current = null;
    touchStartYRef.current = null;
    setDragX(0);
    setIsDragging(false);
    shouldIgnoreRef.current = false;
    hasMovedRef.current = false;
    isHorizontalSwipeRef.current = false;
  };

  const onTouchStart = (e: ReactTouchEvent) => {
    const target = e.target as Element;

    if (isInteractiveElement(target)) {
      shouldIgnoreRef.current = true;
      return;
    }

    shouldIgnoreRef.current = false;
    hasMovedRef.current = false;
    isHorizontalSwipeRef.current = false;

    const touch = e.touches[0];
    touchStartRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const onTouchMove = (e: ReactTouchEvent) => {
    if (
      shouldIgnoreRef.current ||
      touchStartRef.current === null ||
      touchStartYRef.current === null
    ) {
      return;
    }

    const touch = e.touches[0];
    const currentTouch = touch.clientX;
    const currentTouchY = touch.clientY;

    const distanceX = Math.abs(currentTouch - touchStartRef.current);
    const distanceY = Math.abs(currentTouchY - touchStartYRef.current);

    if (!hasMovedRef.current && distanceX < 15 && distanceY < 15) {
      return;
    }

    if (!hasMovedRef.current) {
      hasMovedRef.current = true;
      if (distanceX > distanceY * 1.5) {
        console.log('👆 Horizontal swipe start');
        isHorizontalSwipeRef.current = true;
        setIsDragging(true);
      } else {
        shouldIgnoreRef.current = true;
        return;
      }
    }

    if (isHorizontalSwipeRef.current) {
      e.preventDefault();
      e.stopPropagation();
      const distance = currentTouch - touchStartRef.current;
      setDragX(distance);

      if (onSwipeProgress) {
        const progress = Math.min(Math.abs(distance) / minSwipeDistance, 1);
        onSwipeProgress(progress);
      }
    }
  };

  const onTouchEnd = () => {
    if (
      shouldIgnoreRef.current ||
      touchStartRef.current === null ||
      !hasMovedRef.current
    ) {
      resetState();
      return;
    }

    const distance = dragX;
    const threshold = minSwipeDistance;

    if (distance > threshold && onSwipeRight) {
      onSwipeRight();
    } else if (distance < -threshold && onSwipeLeft) {
      onSwipeLeft();
    }

    resetState();
  };

  return {
    dragX,
    isDragging,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}
